import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid,
} from "recharts";
import { Mail, AlertTriangle, CheckCircle2, Building2, Zap, RefreshCw } from "lucide-react";

const STATUS_COLOR = {
  sent: "#059669",
  failed: "#e11d48",
  skipped_no_poc: "#d97706",
};

const TRIGGER_LABEL = {
  ticket_created: "Ticket created",
  ticket_reminder: "Reminder",
  ticket_sla_breach: "SLA breach",
  ticket_cancellation: "Cancellation",
  ticket_dept_resolve: "Dept resolve link",
  daily_dept_report: "Daily report",
  weekly_dept_report: "Weekly report",
  monthly_dept_report: "Monthly report",
  sla_summary_report: "SLA summary",
  org_level_report: "Org report",
};

function fmtTrigger(t) { return TRIGGER_LABEL[t] || t; }
function fmtPct(num, total) {
  if (!total) return "—";
  return `${Math.round((num / total) * 100)}%`;
}
function fmtDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function EmailDeliveryDashboard() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/reports/email-delivery?days=${days}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const t = data?.totals;
  const successRate = useMemo(() => {
    if (!t || !t.total) return 0;
    return Math.round((t.sent / t.total) * 100);
  }, [t]);

  return (
    <div className="space-y-6" data-testid="email-delivery-page">
      {/* Header + controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl text-slate-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            Email Delivery
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Operational email dispatch health — captured from <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px]">email_log</code> across all triggers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[150px]" data-testid="email-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={load} disabled={busy}
                  className="px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="email-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {err}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total dispatched" value={t?.total ?? "—"}
             icon={<Mail className="w-4 h-4" />} tone="indigo" testid="kpi-total" />
        <Kpi label="Successful" value={t?.sent ?? "—"} sub={t ? `${successRate}% success rate` : ""}
             icon={<CheckCircle2 className="w-4 h-4" />} tone="emerald" testid="kpi-sent" />
        <Kpi label="Failed (provider)" value={t?.failed ?? "—"}
             icon={<AlertTriangle className="w-4 h-4" />} tone="rose" testid="kpi-failed" />
        <Kpi label="Skipped (no POC)" value={t?.skipped_no_poc ?? "—"}
             icon={<Zap className="w-4 h-4" />} tone="amber" testid="kpi-skipped" />
      </div>

      {/* Volume chart */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            Dispatch volume <span className="text-xs font-normal text-slate-400">· last {days} day{days === 1 ? "" : "s"}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer>
            <AreaChart data={data?.by_day || []}>
              <defs>
                <linearGradient id="g-sent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={STATUS_COLOR.sent} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={STATUS_COLOR.sent} stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="g-failed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={STATUS_COLOR.failed} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={STATUS_COLOR.failed} stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="g-skipped" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={STATUS_COLOR.skipped_no_poc} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={STATUS_COLOR.skipped_no_poc} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }}
                     tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="sent" stroke={STATUS_COLOR.sent}
                    fillOpacity={1} fill="url(#g-sent)" strokeWidth={2} name="Sent" />
              <Area type="monotone" dataKey="failed" stroke={STATUS_COLOR.failed}
                    fillOpacity={1} fill="url(#g-failed)" strokeWidth={2} name="Failed" />
              <Area type="monotone" dataKey="skipped_no_poc" stroke={STATUS_COLOR.skipped_no_poc}
                    fillOpacity={1} fill="url(#g-skipped)" strokeWidth={2} name="Skipped (no POC)" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Two-column: dept breakdown + by trigger */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-slate-500" /> By department
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DeptTable rows={data?.by_department || []} />
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              By trigger
            </CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer>
              <BarChart data={(data?.by_trigger || []).slice(0, 10)} layout="vertical" margin={{ left: 18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                <YAxis type="category" dataKey="trigger" tick={{ fontSize: 11, fill: "#475569" }}
                       width={140} tickFormatter={fmtTrigger} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} labelFormatter={fmtTrigger} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="sent" stackId="a" fill={STATUS_COLOR.sent} name="Sent" />
                <Bar dataKey="failed" stackId="a" fill={STATUS_COLOR.failed} name="Failed" />
                <Bar dataKey="skipped_no_poc" stackId="a" fill={STATUS_COLOR.skipped_no_poc} name="Skipped" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top failing + recent failures */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600" /> Recent dispatch failures
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <FailuresTable rows={data?.recent_failures || []} />
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, icon, tone, testid }) {
  const toneClass = {
    indigo: "bg-indigo-50 text-indigo-700 ring-indigo-100",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    rose: "bg-rose-50 text-rose-700 ring-rose-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
  }[tone] || "bg-slate-50 text-slate-700 ring-slate-200";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ring-1 ${toneClass}`}>{icon}</span>
      </div>
      <div className="mt-2 text-3xl font-heading text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function DeptTable({ rows }) {
  if (!rows.length) return <div className="p-6 text-sm text-slate-400 text-center">No dispatches yet in this window.</div>;
  return (
    <div className="max-h-72 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-slate-50/95 backdrop-blur text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-4 py-2.5">Department</th>
            <th className="text-right px-2 py-2.5">Sent</th>
            <th className="text-right px-2 py-2.5">Failed</th>
            <th className="text-right px-2 py-2.5">Skipped</th>
            <th className="text-right px-4 py-2.5">Success</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const issues = r.failed + r.skipped_no_poc;
            return (
              <tr key={r.department_name} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-4 py-2.5 text-slate-700">{r.department_name}</td>
                <td className="px-2 py-2.5 text-right font-mono text-emerald-700">{r.sent}</td>
                <td className="px-2 py-2.5 text-right font-mono text-rose-700">{r.failed || "—"}</td>
                <td className="px-2 py-2.5 text-right font-mono text-amber-700">{r.skipped_no_poc || "—"}</td>
                <td className={`px-4 py-2.5 text-right font-mono ${issues ? "text-slate-700" : "text-emerald-700"}`}>
                  {fmtPct(r.sent, r.total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FailuresTable({ rows }) {
  if (!rows.length) {
    return (
      <div className="p-6 text-sm text-emerald-700 text-center bg-emerald-50/40 inline-flex w-full items-center justify-center gap-2">
        <CheckCircle2 className="w-4 h-4" /> No dispatch failures in this window — clean run.
      </div>
    );
  }
  return (
    <div className="max-h-96 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-slate-50/95 backdrop-blur text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-4 py-2.5">When</th>
            <th className="text-left px-2 py-2.5">Trigger</th>
            <th className="text-left px-2 py-2.5">Ticket</th>
            <th className="text-left px-2 py-2.5">Department</th>
            <th className="text-left px-2 py-2.5">Status</th>
            <th className="text-left px-4 py-2.5">Error</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.created_at || ""}-${r.ticket_id || i}-${r.trigger}`} className="border-t border-slate-100 hover:bg-slate-50/60">
              <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap font-mono text-xs">{fmtDateShort(r.created_at)}</td>
              <td className="px-2 py-2.5"><Badge variant="outline" className="font-mono text-[10px]">{fmtTrigger(r.trigger)}</Badge></td>
              <td className="px-2 py-2.5 font-mono text-xs text-indigo-700">
                {r.ticket_id ? <a href={`/tickets/${r.ticket_id}`} className="hover:underline">{r.ticket_code || r.ticket_id.slice(0, 8)}</a> : "—"}
              </td>
              <td className="px-2 py-2.5 text-slate-700">{r.department_name}</td>
              <td className="px-2 py-2.5">
                {r.status === "failed"
                  ? <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">Failed</Badge>
                  : <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">No POC</Badge>}
              </td>
              <td className="px-4 py-2.5 text-xs text-slate-600 max-w-md truncate" title={r.error}>{r.error || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
